/**
 * Conversion utilities for measurement units with improved precision
 */
import { CONVERSION_FACTORS, PRECISION } from './measurement-constants';

/**
 * Height conversion functions with improved precision
 */
export const heightConversions = {
  cmToFeet: (cm: number) => {
    const totalInches = cm / CONVERSION_FACTORS.CM_PER_INCH;
    const feet = Math.floor(totalInches / CONVERSION_FACTORS.INCHES_PER_FOOT);
    const inches =
      Math.round((totalInches % CONVERSION_FACTORS.INCHES_PER_FOOT) * 10) / 10;

    return { feet, inches };
  },

  feetToCm: (feet: number, inches: number) => {
    const totalInches = feet * CONVERSION_FACTORS.INCHES_PER_FOOT + inches;
    const cm = totalInches * CONVERSION_FACTORS.CM_PER_INCH;

    return (
      Math.round(cm * Math.pow(10, PRECISION.height)) /
      Math.pow(10, PRECISION.height)
    );
  },
};

/**
 * Weight conversion functions with improved precision
 */
export const weightConversions = {
  kgToLbs: (kg: number) => {
    const lbs = kg * CONVERSION_FACTORS.KG_TO_LBS;
    return (
      Math.round(lbs * Math.pow(10, PRECISION.weight)) /
      Math.pow(10, PRECISION.weight)
    );
  },

  lbsToKg: (lbs: number) => {
    const kg = lbs * CONVERSION_FACTORS.LBS_TO_KG;
    return (
      Math.round(kg * Math.pow(10, PRECISION.weight)) /
      Math.pow(10, PRECISION.weight)
    );
  },
};

/**
 * General measurement conversion functions with improved precision
 */
export const measurementConversions = {
  cmToInches: (cm: number) => {
    const inches = cm / CONVERSION_FACTORS.CM_PER_INCH;
    return (
      Math.round(inches * Math.pow(10, PRECISION.measurements)) /
      Math.pow(10, PRECISION.measurements)
    );
  },

  inchesToCm: (inches: number) => {
    const cm = inches * CONVERSION_FACTORS.CM_PER_INCH;
    return (
      Math.round(cm * Math.pow(10, PRECISION.measurements)) /
      Math.pow(10, PRECISION.measurements)
    );
  },
};

/**
 * Combined conversion utilities
 */
export const conversions = {
  ...heightConversions,
  ...weightConversions,
  ...measurementConversions,
};

/**
 * Validate if a conversion is reversible within acceptable tolerance
 */
export function isReversibleConversion(
  original: number,
  convertForward: (n: number) => number,
  convertBack: (n: number) => number,
  tolerance: number = 0.1,
): boolean {
  const converted = convertForward(original);
  const reversed = convertBack(converted);
  return Math.abs(reversed - original) < tolerance;
}
